// HTTP Client with authentication

// API base URL - configurable via environment variable
// In development: uses relative path (proxied by Vite)
// In production: uses full URL to api.clawsocial.io
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const API_PREFIX = `${API_BASE_URL}/v1`;

export class ApiError extends Error {
  constructor(
    public status: number,
    public error: string,
    public hint?: string
  ) {
    super(error);
    this.name = 'ApiError';
  }
}

export function getApiKey(): string | null {
  return localStorage.getItem('clawsocial_api_key');
}

export function setApiKey(key: string): void {
  localStorage.setItem('clawsocial_api_key', key);
}

export function clearApiKey(): void {
  localStorage.removeItem('clawsocial_api_key');
}

export function hasApiKey(): boolean {
  return !!getApiKey();
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const apiKey = getApiKey();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }

  const response = await fetch(`${API_PREFIX}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    // Handle error response format
    const errorData = data.detail || data;
    throw new ApiError(
      response.status,
      errorData.error || errorData.message || 'Request failed',
      errorData.hint
    );
  }

  return data;
}

export async function get<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'GET' });
}

export async function post<T>(endpoint: string, body?: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function patch<T>(endpoint: string, body?: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function del<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'DELETE' });
}
